// src/api/productsApi.js — Products collection CRUD + inventory + prospect matching

import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, where, serverTimestamp, setDoc, getDoc, onSnapshot
} from 'firebase/firestore';
import { db } from '../utils/firebase';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ==================== PRODUCTS CRUD ====================

export const getSalonProducts = async (salonId) => {
  const q = query(collection(db, 'products'), where('salonId', '==', salonId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addSalonProduct = async (salonId, productData) => {
  const productId = generateUUID();
  const docRef = doc(db, 'products', productId);
  await setDoc(docRef, {
    ...productData,
    productId,
    salonId,
    stockLevel: productData.stockLevel ?? null,
    stockRenewalDate: productData.stockRenewalDate || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return productId;
};

export const updateSalonProduct = async (salonId, productId, productData) => {
  const docRef = doc(db, 'products', productId);
  await updateDoc(docRef, {
    ...productData,
    updatedAt: serverTimestamp()
  });
};

export const deleteSalonProduct = async (salonId, productId) => {
  await deleteDoc(doc(db, 'products', productId));
};

// ==================== INVENTORY ====================

export const updateProductInventory = async (salonId, productId, { stockLevel, stockRenewalDate }) => {
  const docRef = doc(db, 'products', productId);
  await updateDoc(docRef, {
    stockLevel: stockLevel !== '' ? Number(stockLevel) : null,
    stockRenewalDate: stockRenewalDate || null,
    stockUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// ==================== PRODUCT ALERTS ====================

export const upsertProductAlert = async (salonId, productId, productName, type, extra = {}) => {
  const alertId = `${salonId}_${productId}_${type}`;
  const alertRef = doc(db, 'productAlerts', alertId);
  await setDoc(alertRef, {
    salonId,
    productId,
    productName,
    type,
    ...extra,
    resolvedAt: null,
    createdAt: serverTimestamp()
  }, { merge: true });
};

export const resolveProductAlert = async (alertId) => {
  const alertRef = doc(db, 'productAlerts', alertId);
  try {
    await updateDoc(alertRef, { resolvedAt: serverTimestamp() });
  } catch {
    // Alert might not exist yet — safe to ignore
  }
};

export const subscribeToProductAlerts = (salonId, onData) => {
  const q = query(
    collection(db, 'productAlerts'),
    where('salonId', '==', salonId),
    where('resolvedAt', '==', null)
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

// ==================== PROSPECT MATCHING ====================

// Parses a Firestore timestamp / ISO string / number into a JS Date (or null).
function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (val.seconds) return new Date(val.seconds * 1000);
  if (typeof val === 'string') return new Date(val);
  if (typeof val === 'number') return new Date(val < 1e10 ? val * 1000 : val);
  return null;
}

// Returns true if a single transaction record satisfies the given criterion.
function txMatchesCriteria(tx, criterion) {
  switch (criterion.type) {
    case 'spend_product': {
      // Only records where productName is populated
      if (!tx.productName) return false;
      const spend = parseFloat(tx.amount || tx.totalAmount || tx.price || 0);
      return spend > (Number(criterion.threshold) || 0);
    }
    case 'last_purchase_date': {
      // Only records where productName is populated; paidAt <= specified date (on or before)
      if (!tx.productName) return false;
      const paidAt = parseDate(tx.paidAt);
      if (!paidAt) return false;
      const threshold = new Date(criterion.date);
      threshold.setHours(23, 59, 59, 999); // include the full selected day
      return paidAt <= threshold;
    }
    case 'product_buyers': {
      // Only records where productName matches the selected product's name
      if (!tx.productName) return false;
      return (tx.productId === criterion.productId) ||
             (tx.productName && criterion.productName &&
              tx.productName.toLowerCase() === criterion.productName.toLowerCase());
    }
    case 'category_buyers': {
      // productName must be populated; category is resolved via the products lookup
      if (!tx.productName) return false;
      return (tx.productCategory || '').toLowerCase() === (criterion.category || '').toLowerCase();
    }
    default:
      return false;
  }
}

// Evaluates whether a transaction satisfies a multi-criteria chain (primary + extras).
// AND means the SAME record must match both the running result and the new criterion.
// OR means a record matching EITHER the running result OR the new criterion counts.
function txMatchesAllCriteria(tx, primary, extras) {
  let result = txMatchesCriteria(tx, primary);
  for (const extra of extras) {
    if (extra.logicalOp === 'AND') {
      result = result && txMatchesCriteria(tx, extra);
    } else {
      result = result || txMatchesCriteria(tx, extra);
    }
  }
  return result;
}

// Main prospect matching function.
// products array is passed in so we can look up product names for product_buyers criteria.
export const getProductProspects = async (salonId, primary, extras = [], products = []) => {
  const customersSnap = await getDocs(
    query(collection(db, 'customers'), where('salonId', '==', salonId))
  );
  const allCustomers = customersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const total = allCustomers.length;

  const toRow = (c) => ({
    id: c.id,
    displayName: c.displayName || c.name || (c.email ? c.email.split('@')[0] : 'Unknown'),
    email: c.email || ''
  });

  // Resolve product names into criteria objects so txMatchesCriteria can use them
  const resolveCriteria = (c) => {
    if (c.type === 'product_buyers' && c.productId) {
      const p = products.find(p => p.id === c.productId);
      return { ...c, productName: p?.name || '' };
    }
    return c;
  };
  const resolvedPrimary = resolveCriteria(primary);
  const resolvedExtras = extras.map(resolveCriteria);

  // For category_buyers we need to cross-check product categories from the products collection.
  // Fetch all product transactions for this salon (productName populated) once.
  const txSnap = await getDocs(
    query(collection(db, 'transactions'), where('salonId', '==', salonId))
  );
  const transactions = txSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // If any criteria is category_buyers, enrich transactions with productCategory from products
  const needsCategoryLookup =
    resolvedPrimary.type === 'category_buyers' ||
    resolvedExtras.some(e => e.type === 'category_buyers');

  let enrichedTransactions = transactions;
  if (needsCategoryLookup) {
    // Build a map from productName → category using the products array passed in.
    // Fall back to fetching from Firestore if local products array is incomplete.
    const productCategoryMap = {};
    products.forEach(p => {
      if (p.name) productCategoryMap[p.name.toLowerCase()] = p.category || '';
    });
    // Fetch any products not covered by the local list
    if (Object.keys(productCategoryMap).length === 0) {
      const prodSnap = await getDocs(
        query(collection(db, 'products'), where('salonId', '==', salonId))
      );
      prodSnap.docs.forEach(d => {
        const pd = d.data();
        if (pd.name) productCategoryMap[pd.name.toLowerCase()] = pd.category || '';
      });
    }
    enrichedTransactions = transactions.map(tx => ({
      ...tx,
      productCategory: tx.productCategory ||
        (tx.productName ? productCategoryMap[tx.productName.toLowerCase()] || '' : '')
    }));
  }

  // Build set of customerIds (or emails) that have at least one matching transaction
  const matchedCustomerEmails = new Set();
  const matchedCustomerIds = new Set();

  enrichedTransactions.forEach(tx => {
    if (txMatchesAllCriteria(tx, resolvedPrimary, resolvedExtras)) {
      if (tx.customerEmail) matchedCustomerEmails.add(tx.customerEmail.toLowerCase());
      if (tx.customerId) matchedCustomerIds.add(tx.customerId);
    }
  });

  const matched = allCustomers.filter(c =>
    (c.id && matchedCustomerIds.has(c.id)) ||
    (c.email && matchedCustomerEmails.has(c.email.toLowerCase()))
  );

  return { matched: matched.map(toRow), total };
};

// ==================== PAYNOW QR (fetched from salons doc for portability) ====================

export const getProductsPayNowQrUrl = async (salonId) => {
  try {
    const salonSnap = await getDoc(doc(db, 'salons', salonId));
    if (!salonSnap.exists()) return null;
    return salonSnap.data()?.paynowConfig?.qrCodeImageUrl || null;
  } catch {
    return null;
  }
};
